import React, { useCallback } from "react";
import { LoadingIndicator, ErrorMessage } from "@jsq/ds/core";
import { FormRadioButton, FormRadioGroup, FormTextInput } from "@jsq/ds/forms";
import { FieldGroup } from "../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../common/components/Form/Layout/MultiInputFormRow";
import { SimpleFormRow } from "../../../../common/components/Form/Layout/SimpleFormRow";
import { PaymentMethod } from "../../../../common/types/enums";
import {
  AchAccountOwnershipType,
  BankAccountType,
} from "../../../../graphql/supergraphOperations";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import { ACHBankDetails } from "./BankDetails";
import {
  AchPaymentPrefInputFields,
  AchPaymentPrefInputSchema,
  type AchPaymentPrefInputType,
} from "./constants";
import { mapContextStateToFormState, mapFormStateToContextState } from "./utils";

export const AchPaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName,
}) => {
  const { setPaymentPrefDetail, workflowMode } = useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();

  const handleFormSubmission = useCallback(
    (values: AchPaymentPrefInputType) => {
      setPaymentPrefDetail(mapFormStateToContextState(values));
      onContinueClick();
    },
    [setPaymentPrefDetail, onContinueClick],
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.ACH}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "AchPaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
      )}
      validationSchema={AchPaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <ACHBankDetails />
      <FieldGroup title="Account details">
        <SimpleFormRow>
          <FormTextInput
            label="Account holder name"
            isFullWidth
            name={AchPaymentPrefInputFields.AccountHolderName}
          />
        </SimpleFormRow>
        <MultiInputFormRow>
          <FormTextInput
            label="Account number"
            isFullWidth
            name={AchPaymentPrefInputFields.AccountNumber}
          />
          <FormTextInput
            label="Confirm account number"
            isFullWidth
            name={AchPaymentPrefInputFields.ConfirmAccountNumber}
          />
        </MultiInputFormRow>
        <MultiInputFormRow>
          <FormRadioGroup name={AchPaymentPrefInputFields.AccountType} label="Account type">
            <FormRadioButton value={BankAccountType.Checking}>Checking</FormRadioButton>
            <FormRadioButton value={BankAccountType.Savings}>Savings</FormRadioButton>
          </FormRadioGroup>
          <FormRadioGroup name={AchPaymentPrefInputFields.OwnershipType} label="Ownership type">
            <FormRadioButton value={AchAccountOwnershipType.Personal}>
              Personal account
            </FormRadioButton>
            <FormRadioButton value={AchAccountOwnershipType.Business}>
              Business account
            </FormRadioButton>
          </FormRadioGroup>
        </MultiInputFormRow>
        <SimpleFormRow>
          <FormTextInput
            label="Additional instructions"
            isFullWidth
            name={AchPaymentPrefInputFields.AdditionalInstructions}
            placeholder="E.g. beneficiary information"
            helpText="Optional"
          />
        </SimpleFormRow>
      </FieldGroup>
    </PaymentPrefLayoutForm>
  );
};
